/**
 * The section list: renders a SowSectionRow per logical section, wires native
 * drag-to-reorder (within a page section), and - in edit mode - an "Add section"
 * control. All mutations are lifted to the workbench via intent callbacks.
 */

import { Button, PillRadio } from '@protoss/ui';
import * as React from 'react';
import type { DocHeading, PreviewSample } from '../../../../components/doc';
import {
  CONTENT_TYPE_META,
  SELECTABLE_CONTENT_TYPES,
  type LogicalContentType,
  type LogicalSection,
} from '../../lib/sowContract';
import { SowSectionRow } from './SowSectionRow';

export interface SowSectionsAccordionProps {
  sections: LogicalSection[];
  isEditing: boolean;
  selectedId: string | null;
  expandedIds: Set<string>;
  sample: PreviewSample;
  headings: DocHeading[];
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onRename: (id: string, text: string) => void;
  onSwitchType: (id: string, type: LogicalContentType) => void;
  onChange: (section: LogicalSection) => void;
  onSampleChange: (sample: PreviewSample) => void;
  onMove: (id: string, dir: 'up' | 'down') => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  onReorder: (id: string, toIndex: number) => void;
  onAdd: (type: LogicalContentType, afterId?: string) => void;
}

export function SowSectionsAccordion(props: SowSectionsAccordionProps) {
  const { sections, isEditing } = props;
  const dragId = React.useRef<string | null>(null);
  const [dragOverId, setDragOverId] = React.useState<string | null>(null);

  const handleDrop = (targetId: string) => {
    const source = dragId.current;
    const toIndex = sections.findIndex((s) => s.id === targetId);
    if (source && source !== targetId && toIndex >= 0) props.onReorder(source, toIndex);
    dragId.current = null;
    setDragOverId(null);
  };

  return (
    <section className="ptp-sow-sections" aria-label="Sections">
      <header className="ptp-sow-sections__header">
        <span className="ptp-sow-sections__title">Sections</span>
        <span className="ptp-sow-sections__count">{sections.length}</span>
      </header>

      {sections.length === 0 ? (
        <p className="ptp-sow-sections__empty">This template has no sections yet.</p>
      ) : (
        <div className="ptp-sow-sections__list">
          {sections.map((section, i) => (
            <SowSectionRow
              key={section.id}
              section={section}
              isEditing={isEditing}
              selected={props.selectedId === section.id}
              expanded={props.expandedIds.has(section.id)}
              sample={props.sample}
              headings={props.headings}
              canMoveUp={i > 0 && sections[i - 1]!.pageSectionIndex === section.pageSectionIndex}
              canMoveDown={
                i < sections.length - 1 &&
                sections[i + 1]!.pageSectionIndex === section.pageSectionIndex
              }
              isDragOver={dragOverId === section.id}
              onSelect={props.onSelect}
              onToggle={props.onToggle}
              onRename={props.onRename}
              onSwitchType={props.onSwitchType}
              onChange={props.onChange}
              onSampleChange={props.onSampleChange}
              onMove={props.onMove}
              onDuplicate={props.onDuplicate}
              onRemove={props.onRemove}
              onDragStart={(id) => (dragId.current = id)}
              onDragOverRow={setDragOverId}
              onDrop={handleDrop}
              onDragEnd={() => {
                dragId.current = null;
                setDragOverId(null);
              }}
            />
          ))}
        </div>
      )}

      {isEditing ? <AddSectionRow onAdd={(type) => props.onAdd(type)} /> : null}
    </section>
  );
}

function AddSectionRow({ onAdd }: { onAdd: (type: LogicalContentType) => void }) {
  const [open, setOpen] = React.useState(false);

  if (!open) {
    return (
      <div className="ptp-sow-add">
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          + Add section
        </Button>
      </div>
    );
  }

  return (
    <div className="ptp-sow-add ptp-sow-add--open">
      <span className="ptp-sow-add__label">Add a</span>
      <PillRadio
        options={SELECTABLE_CONTENT_TYPES.map((t) => ({ value: t, label: CONTENT_TYPE_META[t].label }))}
        selectedValue={null}
        onSelectionChange={(v) => {
          if (v) {
            onAdd(v as LogicalContentType);
            setOpen(false);
          }
        }}
      />
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}
